/**
 * Merging another device's snapshot into this device's database.
 *
 * Two devices share one company through one cloud folder, and each keeps a
 * full local copy. The remote `bokforing.json` is where the two meet: when a
 * pull finds a snapshot this device did not write, everything in it that is
 * missing here has to land locally — without rewriting a single row of the
 * legal record.
 *
 * That shapes the whole module:
 *
 *  - Verifikationer, accountEvents and periodLocks are append-only, so they
 *    are only ever *added*. Two devices that each minted the next number for
 *    the same year and series cannot both be right, and neither copy may be
 *    rewritten, so that case is not merged at all: the plan carries
 *    `NumberCollision`s and refuses to be applied. Resolving it is the user's
 *    decision (`src/domain/sync/shared-ledger.ts`).
 *  - Working state — inbox items, bank rows, merchant memory, the device
 *    registry — is merged field by field, with a rule per table chosen so the
 *    result does not depend on which of the two devices runs the merge.
 *  - Bytes never travel in a snapshot. Attachments merge as index rows and the
 *    receipt hydration fetches the missing bytes home afterwards, exactly as
 *    after a restore.
 *
 * `planMerge` is pure: it takes this device's export and the downloaded one
 * and returns everything that would change. `applyMergePlan` writes that plan
 * in one transaction. Keeping the two apart makes the rules testable and lets
 * the caller tell the user what arrived (`mergeSummaryText`) before, or
 * without, writing anything.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { NumberCollision } from '@/domain/sync/shared-ledger';
import type {
  Account,
  AccountEvent,
  BankRow,
  BankRowStatus,
  BankStatement,
  Company,
  CompanySettings,
  DeviceRegistration,
  FiscalYear,
  InboxItem,
  InboxStatus,
  MerchantRule,
  PaymentSource,
  PeriodLock,
  Uuid,
  Verifikation,
} from '@/domain/types';
import type { AttachmentIndexEntry, CompanySnapshotV3 } from './backup';

// ---- The plan ----------------------------------------------------------------

export interface MergeSummary {
  verifikationer: number;
  accountEvents: number;
  periodLocks: number;
  fiscalYears: number;
  accounts: number;
  paymentSources: number;
  merchantRules: number;
  bankStatements: number;
  bankRows: number;
  inboxItems: number;
  attachments: number;
  devices: number;
  /** Company + companySettings replacements. */
  settings: number;
}

export function emptySummary(): MergeSummary {
  return {
    verifikationer: 0,
    accountEvents: 0,
    periodLocks: 0,
    fiscalYears: 0,
    accounts: 0,
    paymentSources: 0,
    merchantRules: 0,
    bankStatements: 0,
    bankRows: 0,
    inboxItems: 0,
    attachments: 0,
    devices: 0,
    settings: 0,
  };
}

/** A Dexie `update`: the row stays, the listed fields change. */
export interface RowUpdate<T> {
  id: Uuid;
  changes: Partial<T>;
}

export interface MergeAdds {
  verifikationer: Verifikation[];
  accountEvents: AccountEvent[];
  periodLocks: PeriodLock[];
  fiscalYears: FiscalYear[];
  accounts: Account[];
  paymentSources: PaymentSource[];
  merchantRules: MerchantRule[];
  bankStatements: BankStatement[];
  bankRows: BankRow[];
  inboxItems: InboxItem[];
  /** Index rows; the bytes are fetched home by the receipt hydration. */
  attachments: AttachmentIndexEntry[];
  devices: DeviceRegistration[];
}

/** Whole-row replacements of rows that already exist locally. */
export interface MergePuts {
  company?: Company;
  companySettings?: CompanySettings;
  fiscalYears: FiscalYear[];
  accounts: Account[];
  paymentSources: PaymentSource[];
  merchantRules: MerchantRule[];
  devices: DeviceRegistration[];
}

/** Partial updates of existing rows. */
export interface MergeUpdates {
  bankRows: RowUpdate<BankRow>[];
  inboxItems: RowUpdate<InboxItem>[];
  bankStatements: RowUpdate<BankStatement>[];
  attachments: RowUpdate<AttachmentIndexEntry>[];
}

export interface MergePlan {
  companyId: Uuid;
  /** When non-empty the plan must not be applied. */
  collisions: NumberCollision[];
  adds: MergeAdds;
  puts: MergePuts;
  updates: MergeUpdates;
  summary: MergeSummary;
}

export const MERGE_COLLISION_MESSAGE = 'A merge plan with number collisions must not be applied';

// ---- Small pure helpers ------------------------------------------------------

/**
 * The statuses an inbox item never leaves — the complement of `OPEN_STATUSES`
 * in `src/domain/inbox/service.ts`. A terminal status is a decision the user
 * made on one of the two devices, so it outranks anything the other device was
 * still working on.
 */
const TERMINAL_STATUSES: ReadonlySet<InboxStatus> = new Set<InboxStatus>(['booked', 'skipped', 'private']);

function isTerminal(status: InboxStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** open < linked < booked: a bank row only ever moves forward. */
const BANK_STATUS_RANK: Record<BankRowStatus, number> = { open: 0, linked: 1, booked: 2 };

function indexById<T extends { id: string }>(rows: readonly T[]): Map<string, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

/** JSON with object keys in a fixed order, so "the same row" does not depend on property order. */
function stableJson(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined';
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
}

function sameJson(a: unknown, b: unknown): boolean {
  return stableJson(a) === stableJson(b);
}

function verifikationKey(v: Verifikation): string {
  return `${v.fiscalYearId}|${v.series}|${v.number}`;
}

/** Latest `at` per account number. Absent numbers have never been touched. */
function latestEventByAccount(events: readonly AccountEvent[]): Map<number, string> {
  const out = new Map<number, string>();
  for (const e of events) {
    if (e.accountNumber === null) continue;
    const seen = out.get(e.accountNumber);
    if (seen === undefined || e.at > seen) out.set(e.accountNumber, e.at);
  }
  return out;
}

function maxCounts(a: Record<string, number> | undefined, b: Record<string, number> | undefined): Record<string, number> | undefined {
  if (!a && !b) return undefined;
  const out: Record<string, number> = { ...(a ?? {}) };
  for (const [key, value] of Object.entries(b ?? {})) out[key] = Math.max(out[key] ?? 0, value);
  return out;
}

/**
 * One merchant rule out of two. The counts are the union (a confirmation on
 * either device happened), and the *newer* rule decides what the memory now
 * asserts about the merchant.
 */
function mergeMerchantRule(l: MerchantRule, r: MerchantRule): MerchantRule {
  const merged: MerchantRule = r.lastUsedAt > l.lastUsedAt
    ? { ...l, kind: r.kind, categoryId: r.categoryId, vat: r.vat, paymentSourceId: r.paymentSourceId, displayName: r.displayName, createdFrom: r.createdFrom }
    : { ...l };
  merged.timesUsed = Math.max(l.timesUsed, r.timesUsed);
  merged.lastUsedAt = l.lastUsedAt > r.lastUsedAt ? l.lastUsedAt : r.lastUsedAt;
  const counts = maxCounts(l.categoryCounts, r.categoryCounts);
  if (counts) merged.categoryCounts = counts;
  return merged;
}

/**
 * Which side's view of an inbox item stands.
 *
 * A decision (booked/skipped/private) beats work in progress, whichever device
 * made it; `booked` beats the other two, because a verifikation exists and the
 * item has to point at it. Otherwise the later edit wins, and a tie stays
 * local — this device already shows that, so nothing is gained by rewriting it.
 */
function remoteItemWins(l: InboxItem, r: InboxItem): boolean {
  const localTerminal = isTerminal(l.status);
  const remoteTerminal = isTerminal(r.status);
  if (localTerminal !== remoteTerminal) return remoteTerminal;
  if (localTerminal && remoteTerminal && l.status !== r.status) {
    if (r.status === 'booked') return true;
    if (l.status === 'booked') return false;
  }
  return r.updatedAt > l.updatedAt;
}

// ---- The planner -------------------------------------------------------------

/**
 * What it would take for `local` to include everything in `remote`. Pure, and
 * never mutates its inputs.
 */
export function planMerge(local: CompanySnapshotV3, remote: CompanySnapshotV3): MergePlan {
  if (local.company.id !== remote.company.id) {
    throw new Error(`Cannot merge two different companies: ${local.company.id} and ${remote.company.id}`);
  }
  const companyId = local.company.id;
  const collisions: NumberCollision[] = [];
  const adds: MergeAdds = {
    verifikationer: [],
    accountEvents: [],
    periodLocks: [],
    fiscalYears: [],
    accounts: [],
    paymentSources: [],
    merchantRules: [],
    bankStatements: [],
    bankRows: [],
    inboxItems: [],
    attachments: [],
    devices: [],
  };
  const puts: MergePuts = { fiscalYears: [], accounts: [], paymentSources: [], merchantRules: [], devices: [] };
  const updates: MergeUpdates = { bankRows: [], inboxItems: [], bankStatements: [], attachments: [] };

  // 1. Verifikationer: append-only, so union by id — unless the two copies
  //    minted the same number, which no merge can undo.
  const localVerById = indexById(local.verifikationer);
  const localVerByNumber = new Map<string, Verifikation>();
  for (const v of local.verifikationer) if (!localVerByNumber.has(verifikationKey(v))) localVerByNumber.set(verifikationKey(v), v);
  for (const r of remote.verifikationer) {
    if (localVerById.has(r.id)) continue;
    const clash = localVerByNumber.get(verifikationKey(r));
    if (clash) {
      collisions.push({ fiscalYearId: r.fiscalYearId, series: r.series, number: r.number, localId: clash.id, remoteId: r.id });
      continue;
    }
    adds.verifikationer.push(r);
  }

  // 2. The other two append-only tables: union by id, nothing to decide.
  const localEventIds = new Set(local.accountEvents.map((e) => e.id));
  for (const r of remote.accountEvents) if (!localEventIds.has(r.id)) adds.accountEvents.push(r);
  const localLockIds = new Set(local.periodLocks.map((p) => p.id));
  for (const r of remote.periodLocks) if (!localLockIds.has(r.id)) adds.periodLocks.push(r);

  // 3. Fiscal years. Closing a year is one-way, so a closed year wins; an open
  //    one never reopens a year this device has already closed.
  const localYearById = indexById(local.fiscalYears);
  for (const r of remote.fiscalYears) {
    const l = localYearById.get(r.id);
    if (!l) {
      adds.fiscalYears.push(r);
      continue;
    }
    if (r.status === 'closed' && l.status !== 'closed') puts.fiscalYears.push(r);
  }

  // 4. Accounts. The account events are the log of every activation, rename
  //    and deactivation, so "whose chart of accounts is newer for this
  //    number" is answered by the events rather than by a timestamp on the row.
  const localAccountById = indexById(local.accounts);
  const localAccountNumbers = new Set(local.accounts.map((a) => a.number));
  const localLatestEvent = latestEventByAccount(local.accountEvents);
  const remoteLatestEvent = latestEventByAccount(remote.accountEvents);
  for (const r of remote.accounts) {
    const l = localAccountById.get(r.id);
    if (!l) {
      // Both devices activated the same BAS number under their own id. One row
      // per (company, number) is the rule, and the local one is already booked
      // on, so the remote row is dropped rather than added beside it.
      if (!localAccountNumbers.has(r.number)) adds.accounts.push(r);
      continue;
    }
    const localAt = localLatestEvent.get(l.number) ?? '';
    const remoteAt = remoteLatestEvent.get(r.number) ?? '';
    if (remoteAt > localAt || (remoteAt === localAt && !sameJson(r, l))) puts.accounts.push(r);
  }

  // 5-6. The two single rows: newest edit wins. A company row without
  //      `updatedAt` predates the field and is older than any dated edit.
  if (remote.companySettings.updatedAt > local.companySettings.updatedAt) {
    puts.companySettings = { ...remote.companySettings, id: companyId, companyId };
  }
  if (remote.company.updatedAt !== undefined && remote.company.updatedAt > (local.company.updatedAt ?? '')) {
    puts.company = remote.company;
  }

  // 7. Payment sources: renames, hidden sources and saved import mappings.
  const localSourceById = indexById(local.paymentSources);
  for (const r of remote.paymentSources) {
    const l = localSourceById.get(r.id);
    if (!l) {
      adds.paymentSources.push(r);
      continue;
    }
    if ((r.updatedAt ?? '') > (l.updatedAt ?? '')) puts.paymentSources.push(r);
  }

  // 8. Merchant memory. Both devices learn from the same shop, so the rules
  //    are matched on the merchant, not only on the id.
  const localRuleById = indexById(local.merchantRules);
  const localRuleByKey = new Map<string, MerchantRule>();
  for (const rule of local.merchantRules) if (!localRuleByKey.has(rule.merchantKey)) localRuleByKey.set(rule.merchantKey, rule);
  for (const r of remote.merchantRules) {
    const l = localRuleById.get(r.id) ?? localRuleByKey.get(r.merchantKey);
    if (!l) {
      adds.merchantRules.push(r);
      continue;
    }
    const merged = mergeMerchantRule(l, r);
    if (!sameJson(merged, l)) puts.merchantRules.push(merged);
  }

  // 9. Bank statements. The rows are what matters; the statement itself only
  //    gains the id of the file the other device archived.
  const localStatementById = indexById(local.bankStatements);
  for (const r of remote.bankStatements) {
    const l = localStatementById.get(r.id);
    if (!l) {
      adds.bankStatements.push(r);
      continue;
    }
    if (!l.driveFileId && r.driveFileId) updates.bankStatements.push({ id: l.id, changes: { driveFileId: r.driveFileId } });
  }

  // 10. Bank rows. The same statement imported on both devices produces two
  //     rows with two ids but one fingerprint, so the fingerprint is what
  //     identifies a row across devices. Every resolved pair goes into
  //     `rowMap`, which the inbox items and the derived bookings then follow.
  const localRowById = indexById(local.bankRows);
  const localRowByFingerprint = new Map<string, BankRow>();
  for (const row of local.bankRows) if (!localRowByFingerprint.has(row.fingerprint)) localRowByFingerprint.set(row.fingerprint, row);
  const rowMap = new Map<Uuid, Uuid>();
  /** Projected local rows; the updates are the diff against `local` at the end. */
  const rowWork = new Map<Uuid, BankRow>();
  /** Remote inbox item ids adopted by a local row, resolved once rule 11 has run. */
  const rowWantsItem = new Map<Uuid, Uuid>();
  const projectRow = (l: BankRow): BankRow => {
    const seen = rowWork.get(l.id);
    if (seen) return seen;
    const next: BankRow = { ...l, linkedVerifikationIds: [...(l.linkedVerifikationIds ?? [])] };
    rowWork.set(l.id, next);
    return next;
  };
  const localRowIdFor = (id: Uuid): Uuid | undefined => rowMap.get(id) ?? (localRowById.has(id) ? id : undefined);
  const mapRowId = (id: Uuid): Uuid => rowMap.get(id) ?? id;
  for (const r of remote.bankRows) {
    const l = localRowById.get(r.id) ?? localRowByFingerprint.get(r.fingerprint);
    if (!l) {
      adds.bankRows.push(r);
      continue;
    }
    rowMap.set(r.id, l.id);
    const next = projectRow(l);
    if (BANK_STATUS_RANK[r.status] > BANK_STATUS_RANK[next.status]) next.status = r.status;
    for (const id of r.linkedVerifikationIds ?? []) if (!next.linkedVerifikationIds.includes(id)) next.linkedVerifikationIds.push(id);
    if (!next.inboxItemId && r.inboxItemId) rowWantsItem.set(l.id, r.inboxItemId);
  }

  // 11. Inbox items. A receipt that reached both devices through the cloud
  //     folder is one file with two local ids, so `driveFileId` identifies it;
  //     a bank-row item is identified by the row it belongs to.
  const localItemById = indexById(local.inboxItems);
  const remoteItemById = indexById(remote.inboxItems);
  const localItemByDriveFile = new Map<string, InboxItem>();
  const localItemByRow = new Map<Uuid, InboxItem>();
  for (const item of local.inboxItems) {
    if (item.driveFileId && !localItemByDriveFile.has(item.driveFileId)) localItemByDriveFile.set(item.driveFileId, item);
    if (item.bankRowId && !localItemByRow.has(item.bankRowId)) localItemByRow.set(item.bankRowId, item);
  }
  const itemMap = new Map<Uuid, Uuid>();
  const itemChanges = new Map<Uuid, Partial<InboxItem>>();
  const changesFor = (id: Uuid): Partial<InboxItem> => {
    const seen = itemChanges.get(id);
    if (seen) return seen;
    const fresh: Partial<InboxItem> = {};
    itemChanges.set(id, fresh);
    return fresh;
  };
  const localItemIdFor = (id: Uuid): Uuid | undefined => itemMap.get(id) ?? (localItemById.has(id) ? id : undefined);
  /*
   * `processing` means an AI read was running on the other device. Nothing is
   * running here, so the copy that lands is simply unread again.
   */
  const settledStatus = (status: InboxStatus): InboxStatus => (status === 'processing' ? 'new' : status);
  for (const r of remote.inboxItems) {
    const l =
      localItemById.get(r.id) ??
      (r.driveFileId ? localItemByDriveFile.get(r.driveFileId) : undefined) ??
      (r.kind === 'bank_row' && r.bankRowId ? localItemByRow.get(mapRowId(r.bankRowId)) : undefined);
    if (!l) {
      const copy: InboxItem = {
        ...r,
        status: settledStatus(r.status),
        matchedBankRowIds: (r.matchedBankRowIds ?? []).map(mapRowId),
      };
      if (r.bankRowId) copy.bankRowId = mapRowId(r.bankRowId);
      adds.inboxItems.push(copy);
      continue;
    }
    itemMap.set(r.id, l.id);
    if (!remoteItemWins(l, r)) continue;
    const changes = changesFor(l.id);
    /*
     * Only what the other device decided, and only where it differs. An
     * optional field the remote does not have is left alone rather than
     * written as `undefined`, which Dexie would store as a present, empty one.
     */
    const take = <K extends keyof InboxItem>(key: K, value: InboxItem[K] | undefined): void => {
      if (value === undefined || sameJson(value, l[key])) return;
      changes[key] = value as Partial<InboxItem>[K];
    };
    take('status', settledStatus(r.status));
    take('tier', r.tier);
    take('tierReasons', r.tierReasons);
    take('flags', r.flags);
    take('proposal', r.proposal);
    take('reading', r.reading);
    take('verifikationId', r.verifikationId);
    take('duplicateOfVerifikationId', r.duplicateOfVerifikationId);
    take('duplicateOfItemId', r.duplicateOfItemId);
    take('matchedBankRowIds', (r.matchedBankRowIds ?? []).map(mapRowId));
    take('error', r.error);
    take('errorTechnical', r.errorTechnical);
    take('note', r.note);
    // The pile's working state: what the AI read (never re-read), what the
    // user decided and ruled out, and which documents were folded together.
    take('extraction', r.extraction);
    take('classification', r.classification);
    take('decided', r.decided);
    take('rejectedMatches', r.rejectedMatches);
    take('mergedIntoItemId', r.mergedIntoItemId);
    take('extraAttachmentIds', r.extraAttachmentIds);
    take('verifikationIds', r.verifikationIds);
    take('batchId', r.batchId);
    take('updatedAt', r.updatedAt);
  }

  // 12. Attachments travel without their bytes; an existing row keeps the ones
  //     it has and only learns where the file was archived.
  const localAttachmentById = indexById(local.attachmentsIndex);
  for (const r of remote.attachmentsIndex) {
    const l = localAttachmentById.get(r.id);
    if (!l) {
      adds.attachments.push(r);
      continue;
    }
    if (!l.driveFileId && r.driveFileId) updates.attachments.push({ id: l.id, changes: { driveFileId: r.driveFileId } });
  }

  // 13. The device registry: a label per installation, newest sighting wins.
  const localDeviceById = indexById(local.devices);
  for (const r of remote.devices) {
    const l = localDeviceById.get(r.id);
    if (!l) {
      adds.devices.push(r);
      continue;
    }
    if (r.lastSeen > l.lastSeen) puts.devices.push(r);
  }

  /*
   * 14. What the arriving verifikationer imply for the working state on this
   *     device. The other device booked an item and its bank rows and said so
   *     in its own copy of those rows; here the same item may still sit in Att
   *     göra under a different id. Following `meta` is what keeps the two in
   *     step, because `meta` is part of the immutable record and the side
   *     tables are not.
   */
  for (const v of adds.verifikationer) {
    const meta = v.meta;
    if (!meta) continue;
    const itemId = meta.inboxItemId ? localItemIdFor(meta.inboxItemId) : undefined;
    const item = itemId ? localItemById.get(itemId) : undefined;
    if (itemId && item) {
      const pending = itemChanges.get(itemId);
      if (!isTerminal(pending?.status ?? item.status)) {
        const changes = changesFor(itemId);
        changes.status = 'booked';
        changes.verifikationId = v.id;
      }
    }
    // The row the item itself came from is booked; rows that were only matched
    // to it are linked. The item may only exist on the other device, and its
    // own row is then named by the remote copy.
    const booked = item ?? (meta.inboxItemId ? remoteItemById.get(meta.inboxItemId) : undefined);
    const ownRowId = booked?.bankRowId ? localRowIdFor(booked.bankRowId) : undefined;
    for (const rawId of meta.bankRowIds ?? []) {
      const rowId = localRowIdFor(rawId);
      const l = rowId ? localRowById.get(rowId) : undefined;
      if (!rowId || !l) continue;
      const next = projectRow(l);
      if (!next.linkedVerifikationIds.includes(v.id)) next.linkedVerifikationIds.push(v.id);
      if (ownRowId && rowId === ownRowId) next.status = 'booked';
      else if (next.status !== 'booked') next.status = 'linked';
    }
  }

  // The bank rows' diff, once both the row rules and the derived bookings have
  // had their say, so one row never produces two updates.
  for (const [id, next] of rowWork) {
    const base = localRowById.get(id);
    if (!base) continue;
    const wanted = rowWantsItem.get(id);
    // The remote's inbox item may be the local twin of one under another id.
    if (wanted && !next.inboxItemId) next.inboxItemId = itemMap.get(wanted) ?? wanted;
    const changes: Partial<BankRow> = {};
    if (next.status !== base.status) changes.status = next.status;
    if (!sameJson(next.linkedVerifikationIds, base.linkedVerifikationIds)) changes.linkedVerifikationIds = next.linkedVerifikationIds;
    if (next.inboxItemId !== undefined && next.inboxItemId !== base.inboxItemId) changes.inboxItemId = next.inboxItemId;
    if (Object.keys(changes).length > 0) updates.bankRows.push({ id, changes });
  }
  for (const [id, changes] of itemChanges) {
    if (Object.keys(changes).length > 0) updates.inboxItems.push({ id, changes });
  }

  const summary: MergeSummary = {
    verifikationer: adds.verifikationer.length,
    accountEvents: adds.accountEvents.length,
    periodLocks: adds.periodLocks.length,
    fiscalYears: adds.fiscalYears.length + puts.fiscalYears.length,
    accounts: adds.accounts.length + puts.accounts.length,
    paymentSources: adds.paymentSources.length + puts.paymentSources.length,
    merchantRules: adds.merchantRules.length + puts.merchantRules.length,
    bankStatements: adds.bankStatements.length + updates.bankStatements.length,
    bankRows: adds.bankRows.length + updates.bankRows.length,
    inboxItems: adds.inboxItems.length + updates.inboxItems.length,
    attachments: adds.attachments.length + updates.attachments.length,
    devices: adds.devices.length + puts.devices.length,
    settings: (puts.company ? 1 : 0) + (puts.companySettings ? 1 : 0),
  };

  return { companyId, collisions, adds, puts, updates, summary };
}

/** Nothing to write: the local copy already includes everything in the remote one. */
export function isEmptyMerge(plan: MergePlan): boolean {
  if (plan.puts.company || plan.puts.companySettings) return false;
  const lists: readonly { length: number }[] = [
    ...Object.values(plan.adds),
    plan.puts.fiscalYears,
    plan.puts.accounts,
    plan.puts.paymentSources,
    plan.puts.merchantRules,
    plan.puts.devices,
    ...Object.values(plan.updates),
  ];
  return lists.every((list) => list.length === 0);
}

// ---- Applying ----------------------------------------------------------------

let merging = 0;

/**
 * True while `applyMergePlan` runs. Merged rows are not this device's changes,
 * so the backup scheduler's Dexie hooks ignore them, exactly as during a
 * restore (`isRestoring` in backup.ts).
 */
export function isMerging(): boolean {
  return merging > 0;
}

/**
 * Write a plan. One transaction over every table it can touch, so a merge
 * either lands whole or not at all. Refuses a plan with collisions: the two
 * copies disagree about a verifikation number and only the user can say which
 * one stands.
 */
export async function applyMergePlan(plan: MergePlan, db: BookkeepingDB = defaultDb): Promise<void> {
  if (plan.collisions.length > 0) throw new Error(MERGE_COLLISION_MESSAGE);
  merging++;
  try {
    await db.transaction(
      'rw',
      [
        db.companies,
        db.companySettings,
        db.fiscalYears,
        db.accounts,
        db.accountEvents,
        db.verifikationer,
        db.paymentSources,
        db.merchantRules,
        db.periodLocks,
        db.bankStatements,
        db.bankRows,
        db.inboxItems,
        db.attachments,
        db.devices,
      ],
      async () => {
        const { adds, puts, updates } = plan;
        if (adds.fiscalYears.length) await db.fiscalYears.bulkAdd(adds.fiscalYears);
        if (adds.accounts.length) await db.accounts.bulkAdd(adds.accounts);
        if (adds.accountEvents.length) await db.accountEvents.bulkAdd(adds.accountEvents);
        if (adds.verifikationer.length) await db.verifikationer.bulkAdd(adds.verifikationer);
        if (adds.periodLocks.length) await db.periodLocks.bulkAdd(adds.periodLocks);
        if (adds.paymentSources.length) await db.paymentSources.bulkAdd(adds.paymentSources);
        if (adds.merchantRules.length) await db.merchantRules.bulkAdd(adds.merchantRules);
        if (adds.bankStatements.length) await db.bankStatements.bulkAdd(adds.bankStatements);
        if (adds.bankRows.length) await db.bankRows.bulkAdd(adds.bankRows);
        if (adds.inboxItems.length) await db.inboxItems.bulkAdd(adds.inboxItems);
        if (adds.devices.length) await db.devices.bulkAdd(adds.devices);
        // No bytes in a snapshot, so the row arrives empty and the receipt
        // hydration fetches the file home afterwards — as after a restore.
        if (adds.attachments.length) {
          await db.attachments.bulkAdd(adds.attachments.map((a) => ({ ...a, blob: null, hasLocalBlob: 0 as const, thumbnail: new Blob([]) })));
        }

        if (puts.company) await db.companies.put(puts.company);
        if (puts.companySettings) await db.companySettings.put(puts.companySettings);
        if (puts.fiscalYears.length) await db.fiscalYears.bulkPut(puts.fiscalYears);
        if (puts.accounts.length) await db.accounts.bulkPut(puts.accounts);
        if (puts.paymentSources.length) await db.paymentSources.bulkPut(puts.paymentSources);
        if (puts.merchantRules.length) await db.merchantRules.bulkPut(puts.merchantRules);
        if (puts.devices.length) await db.devices.bulkPut(puts.devices);

        for (const u of updates.bankStatements) await db.bankStatements.update(u.id, u.changes);
        for (const u of updates.bankRows) await db.bankRows.update(u.id, u.changes);
        for (const u of updates.inboxItems) await db.inboxItems.update(u.id, u.changes);
        for (const u of updates.attachments) await db.attachments.update(u.id, u.changes);
      },
    );
  } finally {
    merging--;
  }
}

// ---- Telling the user --------------------------------------------------------

/**
 * "3 nya händelser, 2 kvitton och 1 bankrad" — what arrived, in the words the
 * app uses elsewhere (Layer 0: händelse, kvitto, post i Att göra). The tables
 * a person never asked about — account events, locks, the device registry,
 * settings — are not mentioned; they arrive with the rest.
 */
export function mergeSummaryText(summary: MergeSummary): string {
  const parts: string[] = [];
  if (summary.verifikationer > 0) parts.push(summary.verifikationer === 1 ? '1 ny händelse' : `${summary.verifikationer} nya händelser`);
  if (summary.attachments > 0) parts.push(summary.attachments === 1 ? '1 kvitto' : `${summary.attachments} kvitton`);
  if (summary.bankRows > 0) parts.push(summary.bankRows === 1 ? '1 bankrad' : `${summary.bankRows} bankrader`);
  if (summary.inboxItems > 0) parts.push(summary.inboxItems === 1 ? '1 post i Att göra' : `${summary.inboxItems} poster i Att göra`);
  if (summary.accounts > 0) parts.push(summary.accounts === 1 ? '1 konto' : `${summary.accounts} konton`);
  if (parts.length === 0) return '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length === 1) return last;
  return `${parts.slice(0, -1).join(', ')} och ${last}`;
}
